import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { pageEntered, pageTracked } from "../../store/slices/pageViewSlice";
import { getExternalEntryPoint, trackCleavertapEvent } from "../../utils/analyics/analyticsHelper";
import { ANALYTICS_EVENTS } from "../../utils/analyics/analyticsEvents";

const PageViewTracker = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const pageView = useSelector((s: any) => s.pageView);

  const activePageRef = useRef<{
    path: string;
    enteredAt: number;
    data: Record<string, any>;
    entryPoint: string | null;
  } | null>(null);

  const previousPageRef = useRef<string | null>(null);

  /**
   * 🔁 ROUTE CHANGE = EXIT PREVIOUS PAGE
   */
  useEffect(() => {
    const fullPath = `${location.pathname}${location.search}`;

    // EXIT previous page → FIRE EVENT
    if (activePageRef.current?.enteredAt && pageView.status === "ready") {
      const durationSeconds = Math.max(
        1,
        Math.round(
          (Date.now() -
            activePageRef.current.enteredAt) /
            1000
        )
      );

      trackCleavertapEvent(ANALYTICS_EVENTS.Page_Viewed, {
        Page_Path: activePageRef.current.path,
        View_Duration_Seconds: durationSeconds,
        Entry_Point: activePageRef.current.entryPoint || getExternalEntryPoint(),
        ...activePageRef.current.data,
      });
    }

    // ENTER new page → START TIMER (NO EVENT)
    dispatch(pageEntered(fullPath));

    activePageRef.current = {
      path: fullPath,
      enteredAt: Date.now(),
      data: {},
      entryPoint:  getExternalEntryPoint() ||previousPageRef.current
    };
  }, [location.pathname]);
  
  useEffect(() => {
  if (activePageRef.current) {
    previousPageRef.current = activePageRef.current.path;
  }
}, [location.pathname]);

  /**
   * 📦 PAGE READY = STORE DATA
   */
  useEffect(() => {
    if (!activePageRef.current) return;
    if (pageView.status !== "ready") return;

    activePageRef.current.data = pageView.data; // reset enteredAt to now, so that duration is calculated from when page is ready, not when page is entered
  }, [pageView.status, pageView.data]);

  /**
   * 🚪 TAB CLOSE / REFRESH
   */
  useEffect(() => {
    const handleUnload = () => {
      if (!activePageRef.current?.enteredAt || pageView.status !== "ready") return;

      const durationSeconds = Math.max(
        1,
        Math.round(
          (Date.now() -
            activePageRef.current.enteredAt) /
            1000
        )
      );

      trackCleavertapEvent(ANALYTICS_EVENTS.Page_Viewed, {
        Page_Path: activePageRef.current.path,
        View_Duration_Seconds: durationSeconds,
        Entry_Point: activePageRef.current.entryPoint || getExternalEntryPoint(),
        ...activePageRef.current.data,
      });
      dispatch(pageTracked());
    };

    window.addEventListener("beforeunload", handleUnload);
    return () =>
      window.removeEventListener(
        "beforeunload",
        handleUnload
      );
  }, []);

  return null;
};

export default PageViewTracker;
